import { Box, Typography, TypographyProps } from "@mui/material";
import { FormattedDate, FormattedDateWindow } from "utils/DateUtils";
import { DataFormat } from "utils";
import { HistoryValueFragment } from "__generated__/components/entityHistory/graphql/entityHistory.fragments.generated";
import { HistoryValueKind } from "__generated__/enums";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { intl } from "LocaleWrapper";

type HistoryValue = {
	kind: HistoryValueKind.Raw;
	value: unknown;
} | {
	kind: HistoryValueKind.Enum;
	value: string;
	metadata: {
		type: string;
		languageKeyPrefix: string;
		prefix?: string;
		suffix?: string;
	};
} | {
	kind: HistoryValueKind.Number;
	value: number;
	metadata?: {
		prefix?: string;
		suffix?: string;
	};
} | {
	kind: HistoryValueKind.String;
	value: string;
	metadata?: {
		prefix?: string;
		suffix?: string;
	};
} | {
	kind: HistoryValueKind.Boolean;
	value: boolean;
	metadata?: {
		prefix?: string;
		suffix?: string;
	};
} | {
	kind: HistoryValueKind.Date;
	value: string;
	metadata?: {
		prefix?: string;
		suffix?: string;
	};
} | {
	kind: HistoryValueKind.Instant;
	value: number;
	metadata?: {
		prefix?: string;
		suffix?: string;
	};
} | {
	kind: HistoryValueKind.TimeWindow;
	value: {
		from?: number | null;
		to?: number | null;
	};
	metadata?: {
		prefix?: string;
		suffix?: string;
	};
} | {
	kind: HistoryValueKind.Described;
	value: string;
	metadata?: {
		prefix?: string;
		suffix?: string;
	};
} | {
	kind: HistoryValueKind.Entity;
	value: number;
	metadata?: {
		prefix?: string;
		suffix?: string;
	};
} | {
	kind: HistoryValueKind.Collection;
	value: HistoryValue[];
} | {
	kind: HistoryValueKind.Price;
	value: number;
	metadata: {
		currency: string;
		prefix?: string;
		suffix?: string;
	};
};

const typographyProps = (strikeThrough: boolean, overrideProps: TypographyProps): TypographyProps => ({
	variant: "body2",
	...overrideProps,
	sx: {
		...overrideProps.sx,
		textDecoration: strikeThrough ? "line-through" : undefined,
	},
});

const EntityHistoryValue = ({ value, strikeThrough = false, ...rest }: {
	value: HistoryValueFragment | null | undefined;
	strikeThrough?: boolean;
} & TypographyProps) => {
	if (value === null || value === undefined) {
		return renderEmpty();
	}

	const historyValue = value as HistoryValue;
	const prefix = "metadata" in historyValue && historyValue.metadata?.prefix ? interpolateLanguageKeys(historyValue.metadata?.prefix) : undefined;
	const suffix = "metadata" in historyValue && historyValue.metadata?.suffix ? interpolateLanguageKeys(historyValue.metadata?.suffix) : undefined;

	switch (historyValue.kind) {
		case HistoryValueKind.Raw: {
			return (
				<Typography {...typographyProps(strikeThrough, rest)}>
					{renderRaw(historyValue.value, strikeThrough, rest)}
				</Typography>
			);
		}
		case HistoryValueKind.Enum: {
			return (
				<Typography {...typographyProps(strikeThrough, rest)}>
					{prefix} {intl.formatMessage({ id: `${historyValue.metadata.languageKeyPrefix}${historyValue.value}` })} {suffix}
				</Typography>
			);
		}
		case HistoryValueKind.Number: {
			return (
				<Typography {...typographyProps(strikeThrough, rest)}>
					{prefix} {DataFormat.number(historyValue.value)} {suffix}
				</Typography>
			);
		}
		case HistoryValueKind.Price: {
			return (
				<Typography {...typographyProps(strikeThrough, rest)}>
					{prefix} {DataFormat.price(historyValue.value, historyValue.metadata.currency)} {suffix}
				</Typography>
			);
		}
		case HistoryValueKind.String: {
			if (historyValue.value.length === 0) {
				return renderEmpty();
			} else {
				return (
					<Typography {...typographyProps(strikeThrough, rest)}>
						{prefix} {historyValue.value} {suffix}
					</Typography>
				);
			}
		}
		case HistoryValueKind.Boolean: {
			return (
				<Typography {...typographyProps(strikeThrough, rest)}>
					{prefix} {DataFormat.booleanAsText(historyValue.value)} {suffix}
				</Typography>
			);
		}
		case HistoryValueKind.Date:
		case HistoryValueKind.Instant: {
			return (
				<Typography {...typographyProps(strikeThrough, rest)}>
					{prefix} <FormattedDate date={historyValue.value} /> {suffix}
				</Typography>
			);
		}
		case HistoryValueKind.TimeWindow: {
			return (
				<Typography {...typographyProps(strikeThrough, rest)}>
					{prefix} <FormattedDateWindow from={historyValue.value.from} to={historyValue.value.to} /> {suffix}
				</Typography>
			);
		}
		case HistoryValueKind.Described: {
			return (
				<Typography {...typographyProps(strikeThrough, rest)}>
					{prefix} {interpolateLanguageKeys(historyValue.value)} {suffix}
				</Typography>
			);
		}
		case HistoryValueKind.Entity: {
			return (
				<Typography {...typographyProps(strikeThrough, rest)}>
					{prefix} {historyValue.value} {suffix}
				</Typography>
			);
		}
		case HistoryValueKind.Collection: {
			return (
				<Typography {...typographyProps(strikeThrough, rest)}>
					{historyValue.value.map((item, index) => (
						<Box
							key={index}
							display="flex"
							gap={0.5}
							alignItems="baseline"
							flexWrap="wrap"
						>
							<EntityHistoryValue value={item} />
						</Box>
					))}
				</Typography>
			);
		}
	}
};

const renderEmpty = () => (
	<Typography
		variant="body2"
		color="text.disabled"
		component="span"
		display="flex"
		alignItems="center"
	>
		<RemoveRoundedIcon sx={{ fontSize: 18 }} />
	</Typography>
);

const renderRaw = (value: unknown, strikeThrough: boolean, rest: TypographyProps) => {
	if (value === null || value === undefined) {
		return renderEmpty();
	}

	switch (typeof value) {
		case "string": {
			return (
				<EntityHistoryValue
					value={{ kind: HistoryValueKind.String, value, metadata: {} }}
					strikeThrough={strikeThrough}
				/>
			);
		}
		case "bigint":
		case "number": {
			return (
				<EntityHistoryValue
					value={{ kind: HistoryValueKind.Number, value, metadata: {} }}
					strikeThrough={strikeThrough}
				/>
			);
		}
		case "boolean": {
			return (
				<EntityHistoryValue
					value={{ kind: HistoryValueKind.Boolean, value, metadata: {} }}
					strikeThrough={strikeThrough}
				/>
			);
		}
	}

	if (Array.isArray(value)) {
		if (value.length === 0) {
			return renderEmpty();
		}

		return (
			<Box display="flex" flexDirection="column" gap={0.25}>
				{value.map((item, index) => isHistoryValue(item) ? (
					<EntityHistoryValue key={index} value={item} />
				) : renderRaw(item, strikeThrough, rest))}
			</Box>
		);
	}

	if (typeof value === "object") {
		const entries = Object.entries(value as Record<string, unknown>);
		if (entries.length === 0) {
			return renderEmpty();
		}

		return (
			<Box display="flex" flexDirection="column" gap={0.25}>
				{entries.map(([ key, val ]) => (
					<Box
						key={key}
						display="flex"
						gap={0.5}
						alignItems="baseline"
						flexWrap="wrap"
					>
						<Typography variant="caption" color="text.secondary" component="span">
							{intl.formatMessage({ id: key })}:
						</Typography>
						{isHistoryValue(val) ? (
							<EntityHistoryValue
								value={val}
							/>
						) : renderRaw(val, strikeThrough, rest)}
					</Box>
				))}
			</Box>
		);
	}

	return <Typography {...typographyProps(strikeThrough, rest)}>{String(value)}</Typography>;
};

const LANGUAGE_KEY_TOKEN = /\{([A-Z_][A-Z0-9_]*)}/g;

const interpolateLanguageKeys = (template: string): string => template.replace(
	LANGUAGE_KEY_TOKEN,
	(_, key: string) => intl.formatMessage({ id: key }),
);

const isHistoryValue = (value: unknown): value is HistoryValueFragment => {
	if (typeof value !== "object" || value === null) {
		return false;
	}
	return "kind" in value && typeof value.kind === "string" && Object.values(HistoryValueKind).includes(value.kind as HistoryValueKind);
};

export default EntityHistoryValue;
